import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InputIcon from "@mui/icons-material/Input";
import { BOARD_PX_PER_UNIT } from "./skillAgentMoodboardBoard";
import { rotatedImageLayout } from "./skillAgentMoodboardLayout";
import { peekMoodboardAssetBlobUrl } from "./moodboardAssetBlobCache";
import { slotClipLocalGeometry } from "./skillAgentMoodboardTools";

/**
 * Float del pasteboard: mismo cover+zoom+pan (+ clip) que la casilla de mesa.
 * El aspect del marco se conserva al sacar (no clamp independiente w/h).
 */
export default function MoodboardPasteboardItem({
  item,
  bounds,
  fetchBlob,
  selected = false,
  boardPanActive = false,
  onSelect,
  onMove,
  onPlaceOnPage,
  onDropAt,
  onRemove,
  onContextMenu,
}) {
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const [url, setUrl] = useState(() =>
    item?.assetId ? peekMoodboardAssetBlobUrl(item.assetId) : "",
  );
  const [nat, setNat] = useState(null);
  const [boxPx, setBoxPx] = useState({ w: 0, h: 0 });

  useEffect(() => {
    let cancelled = false;
    const peeked = item?.assetId ? peekMoodboardAssetBlobUrl(item.assetId) : "";
    if (peeked) setUrl(peeked);
    (async () => {
      if (!item?.assetId || typeof fetchBlob !== "function") {
        if (!peeked) setUrl("");
        return;
      }
      try {
        const next = await fetchBlob(item.assetId);
        if (cancelled) return;
        setUrl(typeof next === "string" ? next : "");
      } catch {
        if (!cancelled) setUrl("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item?.assetId, fetchBlob]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const apply = () => setBoxPx({ w: el.clientWidth, h: el.clientHeight });
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [url, item.w, item.h]);

  const left = (item.x - bounds.minX) * BOARD_PX_PER_UNIT;
  const top = (item.y - bounds.minY) * BOARD_PX_PER_UNIT;
  // Exacto: mismos board units → mismos px que la casilla (sin mínimo que hinche)
  const w = Math.max(1, item.w * BOARD_PX_PER_UNIT);
  const h = Math.max(1, item.h * BOARD_PX_PER_UNIT);
  const rotation = Number(item.rotation) || 0;
  const fitted =
    nat && boxPx.w > 1 && boxPx.h > 1
      ? rotatedImageLayout(
          nat.w,
          nat.h,
          { x: 0, y: 0, w: boxPx.w, h: boxPx.h },
          {
            zoom: Number(item.zoom) || 1,
            panX: item.panX ?? 0.5,
            panY: item.panY ?? 0.5,
          },
          0,
        )
      : null;

  // Clip local: marco unitario. Legacy página: pageX/Y/W/H.
  const clipIsLocal = String(item.clip?.space || "").toLowerCase() === "local";
  const clipGeom = slotClipLocalGeometry({
    x: clipIsLocal
      ? 0
      : Number.isFinite(Number(item.pageX))
        ? Number(item.pageX)
        : 0,
    y: clipIsLocal
      ? 0
      : Number.isFinite(Number(item.pageY))
        ? Number(item.pageY)
        : 0,
    w: clipIsLocal
      ? 1
      : Number.isFinite(Number(item.pageW))
        ? Number(item.pageW)
        : item.w,
    h: clipIsLocal
      ? 1
      : Number.isFinite(Number(item.pageH))
        ? Number(item.pageH)
        : item.h,
    clip: item.clip || null,
  });
  const clipPathId = `pb-clip-${String(item.id || "x").replace(/\W/g, "")}`;

  const onPointerDown = (e) => {
    if (boardPanActive) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect?.(item.id);
    const worldEl = e.currentTarget.closest?.("[data-board-world]");
    let zoom = 1;
    if (worldEl) {
      const t = window.getComputedStyle(worldEl).transform;
      const m = t && t !== "none" ? t.match(/matrix\(([^,]+)/) : null;
      if (m) {
        const a = Number(m[1]);
        if (Number.isFinite(a) && Math.abs(a) > 0.01) zoom = Math.abs(a);
      }
    }
    dragRef.current = {
      pointerId: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      itemX0: item.x,
      itemY0: item.y,
      zoom: Math.max(0.05, zoom),
      moved: false,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.stopPropagation();
    const z = drag.zoom || 1;
    if (Math.abs(e.clientX - drag.x0) + Math.abs(e.clientY - drag.y0) > 4) {
      drag.moved = true;
    }
    onMove?.({
      x: drag.itemX0 + (e.clientX - drag.x0) / (BOARD_PX_PER_UNIT * z),
      y: drag.itemY0 + (e.clientY - drag.y0) / (BOARD_PX_PER_UNIT * z),
    });
  };

  const onPointerUp = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.stopPropagation();
    const moved = Boolean(drag.moved);
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    if (moved) onDropAt?.(e.clientX, e.clientY, { moved: true });
  };

  return (
    <Box
      data-pasteboard-item="1"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onContextMenu={(e) => {
        if (boardPanActive) return;
        e.preventDefault();
        e.stopPropagation();
        onSelect?.(item.id);
        onContextMenu?.(e, { kind: "pasteboard", itemId: item.id });
      }}
      onDoubleClick={(e) => {
        if (boardPanActive) return;
        e.stopPropagation();
        onPlaceOnPage?.();
      }}
      sx={{
        position: "absolute",
        left,
        top,
        width: w,
        height: h,
        borderRadius: 0.5,
        overflow: "visible",
        bgcolor: "transparent",
        cursor: boardPanActive ? "grab" : "grab",
        touchAction: "none",
        userSelect: "none",
        pointerEvents: boardPanActive ? "none" : "auto",
        zIndex: selected ? 12 : 8,
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        transformOrigin: "center center",
        "&:active": { cursor: "grabbing" },
        "&:hover .pb-actions": { opacity: 1 },
        // Mismo outlineOffset que la casilla (-1) para no parecer más grande
        outline: selected
          ? (t) => `2px solid ${t.palette.warning.main}`
          : "1px solid rgba(251, 191, 36, 0.55)",
        outlineOffset: -1,
        boxShadow: selected ? "0 8px 20px rgba(0,0,0,0.35)" : "none",
      }}
      title="Arrastra al fondo o suelta sobre una mesa · Doble clic → página activa"
    >
      <Box
        ref={boxRef}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: 0.5,
          overflow: "hidden",
          bgcolor: "#ebebeb",
          ...(clipGeom
            ? {
                clipPath: `url(#${clipPathId})`,
                WebkitClipPath: `url(#${clipPathId})`,
              }
            : null),
        }}
      >
        {clipGeom ? (
          <svg
            width={0}
            height={0}
            aria-hidden
            style={{ position: "absolute" }}
          >
            <defs>
              <clipPath id={clipPathId} clipPathUnits="objectBoundingBox">
                {clipGeom.kind === "ellipse" ? (
                  <ellipse
                    cx={clipGeom.cx}
                    cy={clipGeom.cy}
                    rx={clipGeom.rx}
                    ry={clipGeom.ry}
                  />
                ) : clipGeom.kind === "path" ? (
                  <path d={clipGeom.d} />
                ) : (
                  <rect
                    x={clipGeom.x}
                    y={clipGeom.y}
                    width={clipGeom.w}
                    height={clipGeom.h}
                    rx={clipGeom.rx || 0}
                    ry={clipGeom.ry || clipGeom.rx || 0}
                  />
                )}
              </clipPath>
            </defs>
          </svg>
        ) : null}
        {String(item.kind || "").toLowerCase() === "mark" && item.mark ? (
          <Box
            component="svg"
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            sx={{
              width: "100%",
              height: "100%",
              display: "block",
              pointerEvents: "none",
              bgcolor: "#fff",
            }}
          >
            <rect
              x={0.02}
              y={0.02}
              width={0.96}
              height={0.96}
              rx={0.04}
              fill={
                item.mark.fill && item.mark.fill !== "none"
                  ? item.mark.fill
                  : item.mark.color || "#94a3b8"
              }
              fillOpacity={
                item.mark.fill && item.mark.fill !== "none" ? 0.85 : 0.4
              }
              stroke={item.mark.strokeColor || item.mark.color || "#334155"}
              strokeWidth={0.03}
            />
            {item.mark.type === "text" ? (
              <text
                x={0.08}
                y={0.58}
                fill={item.mark.color || "#111"}
                fontSize={0.32}
                fontFamily="sans-serif"
                fontWeight={700}
              >
                {String(item.mark.text || "Aa").slice(0, 10)}
              </text>
            ) : null}
          </Box>
        ) : url ? (
          <Box
            component="img"
            src={url}
            alt=""
            draggable={false}
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img?.naturalWidth && img?.naturalHeight) {
                setNat({ w: img.naturalWidth, h: img.naturalHeight });
              }
            }}
            sx={
              fitted
                ? {
                    position: "absolute",
                    left: fitted.x,
                    top: fitted.y,
                    width: fitted.w,
                    height: fitted.h,
                    objectFit: "fill",
                    pointerEvents: "none",
                    display: "block",
                  }
                : {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    pointerEvents: "none",
                    display: "block",
                  }
            }
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: alpha("#94a3b8", 0.35),
            }}
          />
        )}
      </Box>
      <Box
        className="pb-actions"
        sx={{
          position: "absolute",
          top: -28,
          right: 0,
          display: "flex",
          gap: 0.25,
          opacity: selected ? 1 : 0,
          transition: "opacity 0.15s",
          bgcolor: (t) => alpha(t.palette.common.black, 0.72),
          borderRadius: 1,
          px: 0.25,
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Tooltip title="Meter en página activa">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onPlaceOnPage?.();
            }}
            sx={{ color: "#fff", p: 0.35 }}
          >
            <InputIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar del fondo">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            sx={{ color: "#fff", p: 0.35 }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
